// 🔧 event_settings.js
// ✅ Purpose: Manager/Admin Event Package and Power Up Settings page
// ✅ Features:
// - Shows the included LGV Event Package features
// - Shows optional Power Ups
// - Displays purchased/admin-enabled Power Ups as selected and disabled
// - Prepares purchase buttons for supported Stripe Power Up checkout
// - Uses eventDoc.purchasedAddOns as the source of truth
// - Uses standard success/error notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import {
  LGV_EVENT_PACKAGE,
  LGV_CORE_FEATURES,
  LGV_OPTIONAL_POWER_UPS,
  getDefaultPurchasedAddOns,
} from "../../constants/lgv_features";
import { logger } from "../../utils/logger";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

const EventSettings = () => {
  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [purchasedAddOns, setPurchasedAddOns] = useState(
    getDefaultPurchasedAddOns()
  );

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!eventDoc) return;

    setPurchasedAddOns({
      ...getDefaultPurchasedAddOns(),
      ...(eventDoc.purchasedAddOns || {}),

      // ✅ Backward compatibility while we migrate older events
      auction:
        eventDoc.purchasedAddOns?.auction === true ||
        eventDoc.auctionEnabled === true,
      store:
        eventDoc.purchasedAddOns?.store === true ||
        eventDoc.storeEnabled === true,
      sponsors:
        eventDoc.purchasedAddOns?.sponsors === true ||
        eventDoc.sponsorsEnabled === true ||
        eventDoc.sponsorEnabled === true,
      sponsorPayments:
        eventDoc.purchasedAddOns?.sponsorPayments === true ||
        (eventDoc.demo === true && eventDoc.purchasedAddOns?.sponsors === true) ||
        eventDoc.sponsorPaymentsEnabled === true,
      vendors:
        eventDoc.purchasedAddOns?.vendors === true ||
        eventDoc.vendorsEnabled === true ||
        eventDoc.vendorEnabled === true,
      vendorPayments:
        eventDoc.purchasedAddOns?.vendorPayments === true ||
        (eventDoc.demo === true && eventDoc.purchasedAddOns?.vendors === true) ||
        eventDoc.vendorPaymentsEnabled === true ||
        eventDoc.vendorsPaymentsEnabled === true,
      social:
        eventDoc.purchasedAddOns?.social === true ||
        eventDoc.socialEnabled === true,
    });
  }, [eventDoc]);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get("addon_success") === "1") {
    setShowRefreshButton(true);
          showStatus(setStatus, {
        type: "success",
        message:
            "✅ Power Up checkout completed. If it does not show as enabled yet, refresh in a moment.",
        });

        window.history.replaceState(
        {},
        document.title,
        window.location.pathname
        );
    }

    if (params.get("addon_cancelled") === "1") {
        showStatus(setStatus, {
        type: "warning",
        message: "Power Up checkout was cancelled.",
        });

        window.history.replaceState(
        {},
        document.title,
        window.location.pathname
        );
    }
    }, []);

    const handlePurchaseAddOn = async (selectedAddOnKey) => {
    if (!eventId || !selectedAddOnKey) {
        showStatus(setStatus, {
        type: "error",
        message: "Missing event or Power Up information.",
        });

        return;
    }

    try {
        setSaving(true);

        showStatus(setStatus, {
        type: "info",
        message: "Starting secure checkout...",
        });

        const createCheckout = httpsCallable(
        functions,
        "createAddonCheckoutSession"
        );

        const result = await createCheckout({
        eventId,
        addonKey: selectedAddOnKey,
        });

        const checkoutUrl = result?.data?.url;

        if (!checkoutUrl) {
        throw new Error("Stripe did not return a checkout URL.");
        }

        window.location.href = checkoutUrl;
    } catch (err) {
        logger.error("❌ Failed to start add-on checkout:", {
        value: err,
        });

        showStatus(setStatus, {
        type: "error",
        message:
            err?.message ||
            "Could not start add-on checkout. Please try again.",
        });
    } finally {
        setSaving(false);
    }
    };

  const handleAdminEnableAddOn = async (addOnKey) => {
    if (role !== "admin") {
      showStatus(setStatus, {
        type: "error",
        message: "Only admins can manually enable Power Ups.",
      });

      return;
    }

    try {
      setSaving(true);

      const updatedAddOns = {
        ...getDefaultPurchasedAddOns(),
        ...purchasedAddOns,
        [addOnKey]: true,
      };

      await setDoc(
        doc(db, "events", eventId),
        {
          purchasedAddOns: updatedAddOns,

          // ✅ Backward compatibility fields
          auctionEnabled: updatedAddOns.auction === true,
          storeEnabled: updatedAddOns.store === true,
          ticketsEnabled: updatedAddOns.tickets === true,
          sponsorEnabled: updatedAddOns.sponsors === true,
          sponsorsEnabled: updatedAddOns.sponsors === true,
          vendorEnabled: updatedAddOns.vendors === true,
          vendorsEnabled: updatedAddOns.vendors === true,
          vendorPaymentsEnabled: updatedAddOns.vendorPayments === true,
          vendorsPaymentsEnabled: updatedAddOns.vendorPayments === true,
          sponsorPaymentsEnabled: updatedAddOns.sponsorPayments === true,
          socialEnabled: updatedAddOns.social === true,

          // ✅ Engagement Passport compatibility field
          passportEnabled: updatedAddOns.passport === true,

          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPurchasedAddOns(updatedAddOns);

      showStatus(setStatus, {
        type: "success",
        message: "Power Up enabled successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed to enable add-on:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to enable Power Up.",
      });
    } finally {
      setSaving(false);
    }
  };

  const refreshEventSettings = async () => {
    window.location.reload();
    };

  if (!ready || eventLoading || isManager === null) {
    return <FullPageSpinner message="Loading event settings..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="display-5 mb-2">⚙️ Event Settings</h1>
            <p className="lead text-muted mb-0">
              Review your included LGV Event Package features and manage optional Power Ups.
            </p>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {showRefreshButton && (
            <div className="mb-3">
                <button
                type="button"
                className="btn btn-outline-success"
                onClick={refreshEventSettings}
                >
                🔄 Refresh Power Up Status
                </button>
            </div>
        )}

        <div className="card shadow-sm border-success border-3 mb-4">
          <div className="card-body p-4">
            <div className="row g-4 align-items-center">
              <div className="col-12 col-lg-4">
                <div className="badge bg-success mb-2">
                  Included Package
                </div>

                <h3 className="fw-bold">
                  {LGV_EVENT_PACKAGE.name}
                </h3>

                <div className="display-6 fw-bold text-success">
                  {LGV_EVENT_PACKAGE.priceDisplay}
                </div>

                <div className="text-muted mb-3">
                  {LGV_EVENT_PACKAGE.priceNote}
                </div>

                <p className="text-muted mb-0">
                  {LGV_EVENT_PACKAGE.description}
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <h5 className="fw-bold mb-3">
                  Included Features
                </h5>

                <div className="row g-3">
                  {LGV_CORE_FEATURES.map((feature) => (
                    <div className="col-12 col-md-6" key={feature.key}>
                      <div className="border rounded p-3 h-100 bg-light">
                        <div className="fw-bold mb-1">
                          {feature.icon} {feature.name}
                        </div>

                        <div className="small text-muted">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <strong>⚡ Optional Power Ups</strong>
          </div>

          <div className="card-body">
            <p className="text-muted">
              Active Power Ups are enabled for this event and cannot be purchased again.
            </p>

            <div className="row g-4">
              {LGV_OPTIONAL_POWER_UPS.map((addOn) => {
                const isPurchased = purchasedAddOns?.[addOn.key] === true;
                const canPurchase = Number.isFinite(addOn.price);

                return (
                  <div className="col-12 col-md-6 col-xl-4" key={addOn.key}>
                    <div
                      className={`card h-100 shadow-sm ${
                        isPurchased ? "border-success border-3" : "border"
                      }`}
                    >
                      <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="display-6">
                            {addOn.icon}
                          </div>

                          {isPurchased ? (
                            <span className="badge bg-success">
                              Active
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              Available
                            </span>
                          )}
                        </div>

                        <h5 className="fw-bold">
                          {addOn.name}
                        </h5>

                        <div className="fs-3 fw-bold text-success mb-2">
                          {addOn.priceDisplay}
                        </div>

                        <p className="small text-muted">
                          {addOn.description}
                        </p>

                        <div className="mt-auto d-flex flex-column gap-2">
                          {(isPurchased || canPurchase) && (
                            <button
                              type="button"
                              className={`btn ${
                                isPurchased ? "btn-success" : "btn-outline-success"
                              }`}
                              disabled={isPurchased || saving}
                              onClick={() => handlePurchaseAddOn(addOn.key)}
                            >
                              {isPurchased ? "✓ Enabled" : "Purchase Power Up"}
                            </button>
                          )}

                          {!isPurchased && !canPurchase && role !== "admin" && (
                            <button type="button" className="btn btn-outline-secondary" disabled>
                              Admin activation required
                            </button>
                          )}

                          {role === "admin" && !isPurchased && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={saving}
                              onClick={() => handleAdminEnableAddOn(addOn.key)}
                            >
                              Admin Enable
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventSettings;
